import { useEffect, useState } from 'react'
import { ShieldCheck, ShieldOff, Clock, Loader2 } from 'lucide-react'
import { attendanceAPI } from '../services/api'
import { useAuthStore } from '../store/authStore'
import { useToast, getErrorMessage } from './Toast'

interface Policy {
  mode: 'MONITOR' | 'ENFORCE'
  enforce_from: string | null
  require_training_centre: boolean
  is_enforced_now: boolean
  updated_by_name?: string | null
  updated_at?: string
}

/** ISO string -> value for <input type="datetime-local"> (local time). */
const toLocalInput = (iso: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Admin control for geofence enforcement.
 *   Monitor  - employees can check in/out from anywhere; outside punches are flagged.
 *   Enforce  - outside punches are rejected. Starts now, or at a scheduled time.
 * Viewers see the current state but cannot change it.
 */
export default function GeofencePolicyCard() {
  const { user } = useAuthStore()
  const { success, error: toastError } = useToast()
  const canEdit = user?.role === 'admin'

  const [policy, setPolicy] = useState<Policy | null>(null)
  const [saving, setSaving] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')

  const load = () =>
    attendanceAPI
      .getGeofencePolicy()
      .then((res) => {
        setPolicy(res.data)
        setScheduleAt(toLocalInput(res.data.enforce_from))
      })
      .catch((err) => toastError(getErrorMessage(err, 'Failed to load geofence policy')))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const save = async (data: Parameters<typeof attendanceAPI.updateGeofencePolicy>[0], message: string) => {
    setSaving(true)
    try {
      const res = await attendanceAPI.updateGeofencePolicy(data)
      setPolicy(res.data)
      setScheduleAt(toLocalInput(res.data.enforce_from))
      success(message)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to update geofence policy'))
    } finally {
      setSaving(false)
    }
  }

  if (!policy) return null

  const scheduled = policy.mode === 'ENFORCE' && !policy.is_enforced_now
  const state = policy.is_enforced_now ? 'enforcing' : scheduled ? 'scheduled' : 'monitor'
  const styles = {
    enforcing: 'bg-green-50 border-green-200',
    scheduled: 'bg-amber-50 border-amber-200',
    monitor: 'bg-neutral-50 border-neutral-200',
  }[state]

  return (
    <div className={`mb-6 rounded-lg border p-4 ${styles}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex gap-3">
          {policy.is_enforced_now ? (
            <ShieldCheck className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
          ) : scheduled ? (
            <Clock className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
          ) : (
            <ShieldOff className="w-6 h-6 text-neutral-500 shrink-0 mt-0.5" />
          )}
          <div>
            <h3 className="font-semibold text-neutral-900">Geofence restriction</h3>
            <p className="text-sm text-neutral-700 mt-1">
              {state === 'enforcing' &&
                'ON — check-in and check-out outside the training centre radius are blocked.'}
              {state === 'scheduled' &&
                `Scheduled — blocking starts ${new Date(policy.enforce_from as string).toLocaleString()}. Until then outside punches are only flagged.`}
              {state === 'monitor' &&
                'OFF — employees can check in/out from anywhere; punches outside the radius are allowed but flagged.'}
            </p>
            {policy.updated_by_name && policy.updated_at && (
              <p className="text-xs text-neutral-500 mt-1">
                Last changed by {policy.updated_by_name} on {new Date(policy.updated_at).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {canEdit && (
          <div className="flex flex-col gap-3 md:items-end">
            <div className="flex flex-wrap gap-2">
              <button
                disabled={saving || policy.is_enforced_now}
                onClick={() => save({ mode: 'ENFORCE', enforce_from: null }, 'Geofence restriction started')}
                className="px-3 py-2 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-neutral-300 text-white"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start restriction now'}
              </button>
              <button
                disabled={saving || policy.mode === 'MONITOR'}
                onClick={() => save({ mode: 'MONITOR' }, 'Geofence restriction stopped (monitor only)')}
                className="px-3 py-2 text-sm font-medium rounded-lg border border-neutral-300 hover:bg-white disabled:opacity-50 text-neutral-700"
              >
                Stop restriction
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label className="text-neutral-600">or start at</label>
              <input
                type="datetime-local"
                value={scheduleAt}
                min={toLocalInput(new Date().toISOString())}
                onChange={(e) => setScheduleAt(e.target.value)}
                className="px-2 py-1.5 border border-neutral-300 rounded-lg bg-white"
              />
              <button
                disabled={saving || !scheduleAt}
                onClick={() =>
                  save({ mode: 'ENFORCE', enforce_from: new Date(scheduleAt).toISOString() }, 'Geofence restriction scheduled')
                }
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-primary text-primary hover:bg-primary hover:text-white disabled:opacity-50"
              >
                Schedule
              </button>
            </div>
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={policy.require_training_centre}
                disabled={saving}
                onChange={(e) =>
                  save({ require_training_centre: e.target.checked }, 'Setting updated')
                }
              />
              While enforcing, also block employees with no training centre assigned
            </label>
          </div>
        )}
      </div>
    </div>
  )
}
